import React, { useState, useEffect, useCallback } from 'react';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  FileSpreadsheet, Download, RotateCcw, CheckCircle2, Loader2,
  Eye, Link, Package, Send, FileText, XCircle, Wifi, WifiOff,
  Mail, MailCheck, RefreshCw, CreditCard, CheckSquare, Car, Search,
  BarChart3, TrendingUp, TrendingDown, ChevronLeft, ChevronRight,
  Database, Code, Filter, ArrowUpDown
} from 'lucide-react';
import {
  previewMensualidades, previewServicios, getLotes, generarLote,
  facturarLote, rollbackLote, descargarExcelFacto, linkifyCrearCobros,
  factoStatus, factoEmitirLote, factoListarDtes, factoDescargarPdf,
  factoEmitirNotaCredito, factoSync, getConfig, factoCapturarPatentes,
  factoDashboard, factoRawDocument, factoInfo, factoUpdateEstadoPago,
  factoCapturarPatentesStatus,
} from '../../lib/gestion4satApi';
import { toast } from 'sonner';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const estadoColors = {
  pendiente: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20',
  facturado: 'bg-green-500/10 text-green-600 border-green-500/20',
  parcial: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  revertido: 'bg-red-500/10 text-red-600 border-red-500/20',
};
const tipoLabels = { mensualidades: 'Mensualidades', servicios: 'Servicios', consolidado: 'Consolidado' };
const docTypeOptions = [
  { value: '2', label: 'Factura Electrónica' },
  { value: '32', label: 'Factura Exenta Electrónica' },
  { value: '31', label: 'Factura de Compra Electrónica' },
  { value: '16', label: 'Nota de Crédito Electrónica' },
];
const docTypeLabels = Object.fromEntries(docTypeOptions.map(d => [d.value, d.label]));
const estadoPagoColors = {
  pendiente: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  pagado: 'bg-green-500/10 text-green-600 border-green-500/20',
  desconocido: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};
const siiColors = {
  aceptado: 'bg-green-500/10 text-green-600',
  enviado: 'bg-blue-500/10 text-blue-600',
  rechazado: 'bg-red-500/10 text-red-600',
  no_enviado: 'bg-gray-500/10 text-gray-500',
};

function fmt(n) { return (n || 0).toLocaleString('es-CL'); }
const PAGE_SIZE = 30;

export default function G4sFacturacionPage() {
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);

  // Generator
  const [modo, setModo] = useState('mensualidades');
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());
  const [preview, setPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState(new Set());

  // Lotes
  const [lotes, setLotes] = useState([]);
  const [lotesTotal, setLotesTotal] = useState(0);
  const [lotesLoading, setLotesLoading] = useState(false);
  const [detalleLote, setDetalleLote] = useState(null);

  // Facto
  const [factoConnected, setFactoConnected] = useState(null);
  const [emittingLote, setEmittingLote] = useState(null);
  const [docTypeId, setDocTypeId] = useState('2');
  const [giroProfiles, setGiroProfiles] = useState([]);
  const [selectedGiro, setSelectedGiro] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [createLinkify, setCreateLinkify] = useState(true);

  // Sync
  const [syncDialog, setSyncDialog] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncDateFrom, setSyncDateFrom] = useState('');
  const [syncDateTo, setSyncDateTo] = useState('');
  const [syncResult, setSyncResult] = useState(null);
  const [factoInfoData, setFactoInfoData] = useState(null);

  // DTEs with pagination & filters
  const [dtes, setDtes] = useState([]);
  const [dtesTotal, setDtesTotal] = useState(0);
  const [dtesLoading, setDtesLoading] = useState(false);
  const [dtesPage, setDtesPage] = useState(0);
  const [dtesSearch, setDtesSearch] = useState('');
  const [dtesFilterPago, setDtesFilterPago] = useState('todos');
  const [dtesFilterTipo, setDtesFilterTipo] = useState('all');

  // Dashboard
  const [dashData, setDashData] = useState(null);

  // Emit Dialog
  const [emitDialog, setEmitDialog] = useState(null);

  // Capturar Patentes
  const [capturaDialog, setCapturaDialog] = useState(false);
  const [capturaMes, setCapturaMes] = useState(now.getMonth() + 1);
  const [capturaAnio, setCapturaAnio] = useState(now.getFullYear());
  const [capturando, setCapturando] = useState(false);
  const [capturaResult, setCapturaResult] = useState(null);
  const [capturaDryRun, setCapturaDryRun] = useState(true);
  const [capturaProgress, setCapturaProgress] = useState(null);
  const [excludedPatentes, setExcludedPatentes] = useState(new Set());
  const [includedFiltered, setIncludedFiltered] = useState(new Set());

  // Raw JSON Dialog
  const [rawJsonDialog, setRawJsonDialog] = useState(null);
  const [rawJsonLoading, setRawJsonLoading] = useState(false);
  const [rawJsonData, setRawJsonData] = useState(null);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const d = (modo === 'mensualidades' || modo === 'consolidado')
        ? await previewMensualidades(mes, anio) : await previewServicios();
      setPreview(d);
      setSelected(new Set((d.empresas || []).map((_, i) => i)));
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [modo, mes, anio]);

  const loadLotes = useCallback(async () => {
    setLotesLoading(true);
    try { const d = await getLotes({}); setLotes(d.lotes); setLotesTotal(d.total); }
    catch (e) { toast.error(e.message); }
    setLotesLoading(false);
  }, []);

  const loadDtes = useCallback(async (page = dtesPage) => {
    setDtesLoading(true);
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (dtesSearch) params.search = dtesSearch;
      if (dtesFilterPago && dtesFilterPago !== 'todos') params.estado_pago = dtesFilterPago;
      if (dtesFilterTipo && dtesFilterTipo !== 'all') params.document_type_id = dtesFilterTipo;
      const d = await factoListarDtes(params);
      setDtes(d.dtes);
      setDtesTotal(d.total);
    } catch (e) { toast.error(e.message); }
    setDtesLoading(false);
  }, [dtesPage, dtesSearch, dtesFilterPago, dtesFilterTipo]);

  const checkFacto = useCallback(async () => {
    try { const d = await factoStatus(); setFactoConnected(d.connected); }
    catch { setFactoConnected(false); }
  }, []);

  const loadConfig = useCallback(async () => {
    try { const c = await getConfig(); setGiroProfiles(c.giro_profiles || []); }
    catch { /* ignore */ }
  }, []);

  useEffect(() => { checkFacto(); loadConfig(); }, [checkFacto, loadConfig]);
  useEffect(() => {
    if (tab === 'generar') loadPreview();
    if (tab === 'lotes') loadLotes();
    if (tab === 'dtes') loadDtes();
    if (tab === 'dashboard') {
      factoDashboard().then(setDashData).catch(() => {});
    }
  }, [tab, loadPreview, loadLotes, loadDtes]);

  // Reload DTEs when filters/page change
  useEffect(() => {
    if (tab === 'dtes') loadDtes(dtesPage);
  }, [dtesPage, dtesSearch, dtesFilterPago, dtesFilterTipo]);

  const toggleSelect = (i) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };
  const toggleAll = () => {
    if (!preview?.empresas) return;
    setSelected(prev =>
      prev.size === preview.empresas.length ? new Set() : new Set(preview.empresas.map((_, i) => i))
    );
  };

  const selectedEmpresas = preview?.empresas?.filter((_, i) => selected.has(i)) || [];
  const selectedNeto = selectedEmpresas.reduce((s, e) => s + (e.total_neto || 0), 0);
  const selectedTotal = selectedEmpresas.reduce((s, e) => s + (e.total || 0), 0);

  const handleGenerar = async () => {
    if (selectedEmpresas.length === 0) { toast.error('Selecciona al menos una empresa'); return; }
    setGenerating(true);
    try {
      const empresa_ids = selectedEmpresas.map(e => e.empresa_id).filter(Boolean);
      const payload = { tipo: modo, empresa_ids };
      if (modo !== 'servicios') { payload.mes = mes; payload.anio = anio; }
      const lote = await generarLote(payload);
      toast.success(`Lote ${lote.numero_lote} generado con ${selectedEmpresas.length} empresas`);
      setTab('lotes');
      loadLotes();
    } catch (e) { toast.error(e.message); }
    setGenerating(false);
  };

  const handleEmitirFacto = async (loteId) => {
    setEmittingLote(loteId);
    try {
      const r = await factoEmitirLote(loteId, {
        document_type_id: parseInt(docTypeId),
        giro_profile_id: selectedGiro && selectedGiro !== 'default' ? selectedGiro : undefined,
        send_email: sendEmail,
        create_linkify: createLinkify,
      });
      toast.success(r.message);
      if (r.errores > 0) toast.warning(`${r.errores} errores al emitir`);
      loadLotes();
      loadDtes();
    } catch (e) { toast.error(e.message); }
    setEmittingLote(null);
    setEmitDialog(null);
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const payload = {};
      if (syncDateFrom) payload.date_from = syncDateFrom;
      if (syncDateTo) payload.date_to = syncDateTo;
      const r = await factoSync(payload);
      setSyncResult(r);
      toast.success(r.message);
      loadDtes();
    } catch (e) { toast.error(e.message); }
    setSyncing(false);
  };

  const openSyncDialog = async () => {
    setSyncDialog(true);
    setSyncResult(null);
    try {
      const info = await factoInfo();
      setFactoInfoData(info);
    } catch { /* ignore */ }
  };

  const handleCapturar = async () => {
    setCapturando(true);
    setCapturaResult(null);
    setCapturaProgress(null);
    try {
      const r = await factoCapturarPatentes({ mes: capturaMes, anio: capturaAnio, dry_run: capturaDryRun, max_pages: 20 });
      if (r.task_id) {
        let retries = 0;
        const pollInterval = setInterval(async () => {
          try {
            const status = await factoCapturarPatentesStatus(r.task_id);
            retries = 0;
            setCapturaProgress(status);
            if (status.status === 'done') {
              clearInterval(pollInterval);
              setCapturaResult(status.result);
              setExcludedPatentes(new Set());
              setIncludedFiltered(new Set());
              setCapturaProgress(null);
              setCapturando(false);
              toast.success(status.result.message);
            } else if (status.status === 'error') {
              clearInterval(pollInterval);
              setCapturaProgress(null);
              setCapturando(false);
              toast.error(status.progress || 'Error en la tarea');
            }
          } catch (e) {
            retries++;
            if (retries > 5) {
              clearInterval(pollInterval);
              setCapturaProgress(null);
              setCapturando(false);
              toast.error('La tarea no respondió después de varios intentos');
            }
          }
        }, 3000);
      }
    } catch (e) { toast.error(e.message); setCapturando(false); }
  };

  const handleViewRaw = async (dte) => {
    if (!dte.facto_document_id) { toast.error('Este DTE no tiene ID de Facto'); return; }
    setRawJsonDialog(dte);
    setRawJsonLoading(true);
    setRawJsonData(null);
    try {
      const r = await factoRawDocument(dte.facto_document_id);
      setRawJsonData(r.raw);
    } catch (e) { toast.error(e.message); }
    setRawJsonLoading(false);
  };

  const handleTogglePago = async (dte) => {
    const next = dte.estado_pago === 'pagado' ? 'pendiente' : 'pagado';
    try {
      await factoUpdateEstadoPago(dte.id, next);
      toast.success(`DTE #${dte.document_number} → ${next}`);
      loadDtes(dtesPage);
    } catch (e) { toast.error(e.message); }
  };

  const totalPages = Math.ceil(dtesTotal / PAGE_SIZE);

  return (
    <G4sLayout>
      <div className="space-y-4" data-testid="g4s-facturacion-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Facturación Electrónica</h1>
            <p className="text-sm text-muted-foreground">Genera lotes, emite DTEs vía Facto, envía emails y concilia con Linkify</p>
          </div>
          <div className="flex items-center gap-2">
            {factoConnected === null ? (
              <Badge variant="outline"><Loader2 className="h-3 w-3 animate-spin mr-1" /> Facto</Badge>
            ) : factoConnected ? (
              <Badge className="bg-green-500/10 text-green-600 border-green-500/20" data-testid="facto-connected">
                <Wifi className="h-3 w-3 mr-1" /> Facto API
              </Badge>
            ) : (
              <Badge className="bg-red-500/10 text-red-600 border-red-500/20" data-testid="facto-disconnected">
                <WifiOff className="h-3 w-3 mr-1" /> Sin config
              </Badge>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit flex-wrap">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'generar', label: 'Generar Lote', icon: FileSpreadsheet },
            { id: 'lotes', label: `Historial (${lotesTotal})`, icon: Package },
            { id: 'dtes', label: `DTEs (${dtesTotal})`, icon: FileText },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === t.id ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* ====== TAB: Dashboard ====== */}
        {tab === 'dashboard' && (
          <div className="space-y-4">
            {!dashData ? <Skeleton className="h-40 w-full" /> : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Card><CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs text-muted-foreground font-medium">DTEs este mes</div>
                        <div className="text-3xl font-black tabular-nums">{dashData.this_month.count}</div>
                        <div className="text-xs text-emerald-600 font-mono">${fmt(dashData.this_month.total)}</div>
                      </div>
                      <div className="flex flex-col items-end">
                        {dashData.prev_month.count > 0 && (
                          <Badge variant="outline" className={`text-xs ${dashData.this_month.count >= dashData.prev_month.count ? 'text-green-600' : 'text-red-500'}`}>
                            {dashData.this_month.count >= dashData.prev_month.count ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                            vs {dashData.prev_month.count}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent></Card>

                  <Card><CardContent className="p-4">
                    <div className="text-xs text-muted-foreground font-medium">Pagados</div>
                    <div className="text-3xl font-black tabular-nums text-green-600">{dashData.pagos.pagados.count}</div>
                    <div className="text-xs text-green-600 font-mono">${fmt(dashData.pagos.pagados.total)}</div>
                  </CardContent></Card>

                  <Card><CardContent className="p-4">
                    <div className="text-xs text-muted-foreground font-medium">Pendientes pago</div>
                    <div className="text-3xl font-black tabular-nums text-yellow-600">{dashData.pagos.pendientes.count}</div>
                    <div className="text-xs text-yellow-600 font-mono">${fmt(dashData.pagos.pendientes.total)}</div>
                  </CardContent></Card>

                  <Card><CardContent className="p-4">
                    <div className="text-xs text-muted-foreground font-medium">Tasa Conciliación</div>
                    <div className="text-3xl font-black tabular-nums">{dashData.pagos.tasa_conciliacion}%</div>
                    <div className="text-xs text-muted-foreground">{dashData.totals.emails_sent} emails enviados</div>
                  </CardContent></Card>
                </div>

                {dashData.this_month.by_type.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Desglose por tipo (este mes)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {dashData.this_month.by_type.map((t, i) => (
                          <div key={i} className="flex items-center justify-between py-1 border-b last:border-0">
                            <Badge variant="outline" className="text-xs">{t.label}</Badge>
                            <div className="flex items-center gap-4">
                              <span className="text-sm font-mono">{t.count} docs</span>
                              <span className="text-sm font-mono font-bold">${fmt(t.total)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {dashData.prev_month.count > 0 && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Mes anterior</span>
                        <div className="text-right">
                          <span className="font-bold">{dashData.prev_month.count} DTEs</span>
                          <span className="text-muted-foreground ml-2">— ${fmt(dashData.prev_month.total)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        )}

        {/* ====== TAB: Generar ====== */}
        {tab === 'generar' && (
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Configuración del Lote</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Label>Modalidad</Label>
                    <Select value={modo} onValueChange={v => { setModo(v); setPreview(null); }}>
                      <SelectTrigger data-testid="select-modo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mensualidades">Mensualidades recurrentes</SelectItem>
                        <SelectItem value="servicios">Servicios específicos</SelectItem>
                        <SelectItem value="consolidado">Consolidado (ambos)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {modo !== 'servicios' && (<>
                    <div className="w-36">
                      <Label>Mes</Label>
                      <Select value={String(mes)} onValueChange={v => { setMes(Number(v)); setPreview(null); }}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{MESES.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="w-24">
                      <Label>Año</Label>
                      <Input type="number" value={anio} onChange={e => { setAnio(Number(e.target.value)); setPreview(null); }} />
                    </div>
                  </>)}
                  <div className="flex items-end">
                    <Button onClick={loadPreview} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-preview">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                      Vista Previa
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {preview && (<>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <Card><CardContent className="p-4 text-center">
                  <div className="text-2xl font-black tabular-nums">{selectedEmpresas.length}</div>
                  <div className="text-xs text-muted-foreground">Seleccionadas</div>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <div className="text-lg font-bold tabular-nums text-muted-foreground">{preview.empresas?.length || 0}</div>
                  <div className="text-xs text-muted-foreground">Total empresas</div>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <div className="text-2xl font-black tabular-nums text-emerald-600">${fmt(selectedNeto)}</div>
                  <div className="text-xs text-muted-foreground">Neto selección</div>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <div className="text-lg font-bold tabular-nums">${fmt(Math.round(selectedNeto * 0.19))}</div>
                  <div className="text-xs text-muted-foreground">IVA 19%</div>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <div className="text-2xl font-black tabular-nums">${fmt(selectedTotal)}</div>
                  <div className="text-xs text-muted-foreground">Total</div>
                </CardContent></Card>
              </div>

              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="p-3 w-10">
                          <Checkbox
                            checked={selected.size === (preview.empresas?.length || 0) && selected.size > 0}
                            onCheckedChange={toggleAll}
                            data-testid="select-all"
                          />
                        </th>
                        <th className="text-left p-3 font-semibold">RUT</th>
                        <th className="text-left p-3 font-semibold">Razón Social</th>
                        <th className="text-left p-3 font-semibold hidden md:table-cell">Glosa</th>
                        <th className="text-center p-3 font-semibold">Items</th>
                        <th className="text-right p-3 font-semibold">Neto</th>
                        <th className="text-right p-3 font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.empresas || []).map((e, i) => (
                        <tr key={i} className={`border-b hover:bg-muted/30 ${!selected.has(i) ? 'opacity-40' : ''}`}>
                          <td className="p-3">
                            <Checkbox checked={selected.has(i)} onCheckedChange={() => toggleSelect(i)} />
                          </td>
                          <td className="p-3 font-mono text-xs">{e.rut || e.rut_formateado || '-'}</td>
                          <td className="p-3 text-xs font-medium">{e.razon_social}</td>
                          <td className="p-3 text-xs hidden md:table-cell text-muted-foreground">{e.glosa}</td>
                          <td className="p-3 text-center"><Badge variant="secondary">{e.dispositivos_count || e.cantidad || e.servicios?.length || 0}</Badge></td>
                          <td className="p-3 text-right font-mono">${fmt(e.total_neto)}</td>
                          <td className="p-3 text-right font-mono font-bold">${fmt(e.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  {selectedEmpresas.length} de {preview.empresas?.length} empresas seleccionadas
                </span>
                <Button onClick={handleGenerar} disabled={generating || selectedEmpresas.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-generar">
                  {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
                  Generar Lote ({selectedEmpresas.length} empresas)
                </Button>
              </div>
            </>)}
          </div>
        )}

        {/* ====== TAB: Lotes ====== */}
        {tab === 'lotes' && (
          <div className="space-y-4">
            {lotesLoading ? <Skeleton className="h-40 w-full" /> :
            lotes.length === 0 ? (
              <Card><CardContent className="text-center py-12 text-muted-foreground">
                Sin lotes generados. Ve a Generar Lote para crear uno.
              </CardContent></Card>
            ) : lotes.map(lote => (
              <Card key={lote.id} className="hover:border-emerald-500/20 transition-colors">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold font-mono">{lote.numero_lote}</span>
                        <Badge className={estadoColors[lote.estado] || ''}>{lote.estado}</Badge>
                        <Badge variant="outline" className="text-xs">{tipoLabels[lote.tipo] || lote.tipo}</Badge>
                        {lote.facto_exitosos > 0 && (
                          <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20 text-xs">
                            <FileText className="h-3 w-3 mr-1" />{lote.facto_exitosos} DTEs
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {lote.mes && lote.anio && `${MESES[lote.mes - 1]} ${lote.anio} | `}
                        {lote.total_empresas} empresas | {new Date(lote.created_at).toLocaleString('es-CL')}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-right mr-3">
                        <div className="text-lg font-black tabular-nums">${fmt(lote.total)}</div>
                        <div className="text-xs text-muted-foreground">Neto: ${fmt(lote.total_neto)}</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setDetalleLote(lote)} data-testid={`ver-${lote.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => descargarExcelFacto(lote.id).then(() => toast.success('Excel descargado'))} data-testid={`excel-${lote.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                      {factoConnected && lote.estado === 'pendiente' && (
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setEmitDialog({ loteId: lote.id, lote })} data-testid={`facto-emit-${lote.id}`}>
                          <Send className="h-4 w-4 mr-1" /> Emitir DTE
                        </Button>
                      )}
                      {lote.estado === 'pendiente' && (
                        <Button size="sm" variant="outline" className="text-green-600" onClick={async () => { try { await facturarLote(lote.id); toast.success('Marcado manual'); loadLotes(); } catch(e) { toast.error(e.message); }}}>
                          <CheckCircle2 className="h-4 w-4" />
                        </Button>
                      )}
                      {lote.estado !== 'revertido' && (
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={async () => { try { await rollbackLote(lote.id); toast.success('Revertido'); loadLotes(); } catch(e) { toast.error(e.message); }}}>
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ====== TAB: DTEs ====== */}
        {tab === 'dtes' && (
          <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={openSyncDialog} disabled={syncing} data-testid="btn-sync-facto">
                  {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Database className="h-4 w-4 mr-1" />}
                  Sincronizar desde Facto
                </Button>
                <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => { setCapturaDialog(true); setCapturaResult(null); }} data-testid="btn-capturar-patentes">
                  <Car className="h-4 w-4 mr-1" /> Capturar Patentes
                </Button>
              </div>
              <div className="text-xs text-muted-foreground">
                {dtesTotal} documentos en total
              </div>
            </div>

            {/* Filters */}
            <Card>
              <CardContent className="p-3">
                <div className="flex flex-col sm:flex-row gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Buscar</Label>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="RUT, razón social, folio..."
                        className="pl-9 h-9"
                        value={dtesSearch}
                        onChange={e => { setDtesSearch(e.target.value); setDtesPage(0); }}
                        data-testid="dtes-search"
                      />
                    </div>
                  </div>
                  <div className="w-40">
                    <Label className="text-xs">Estado Pago</Label>
                    <Select value={dtesFilterPago} onValueChange={v => { setDtesFilterPago(v); setDtesPage(0); }}>
                      <SelectTrigger className="h-9" data-testid="dtes-filter-pago"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="pagado">Pagado</SelectItem>
                        <SelectItem value="desconocido">Desconocido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-48">
                    <Label className="text-xs">Tipo Documento</Label>
                    <Select value={dtesFilterTipo} onValueChange={v => { setDtesFilterTipo(v === 'all' ? '' : v); setDtesPage(0); }}>
                      <SelectTrigger className="h-9" data-testid="dtes-filter-tipo"><SelectValue placeholder="Todos" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {docTypeOptions.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => loadDtes(dtesPage)} className="h-9">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {dtesLoading ? <Skeleton className="h-40 w-full" /> :
            dtes.length === 0 ? (
              <Card><CardContent className="text-center py-12 text-muted-foreground">
                Sin DTEs. Emite desde Historial o sincroniza desde Facto.
              </CardContent></Card>
            ) : (
              <>
                <Card>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="dtes-table">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="p-3 text-left font-semibold">Folio</th>
                          <th className="p-3 text-left font-semibold">Tipo</th>
                          <th className="p-3 text-left font-semibold">Empresa</th>
                          <th className="p-3 text-left font-semibold hidden md:table-cell">RUT</th>
                          <th className="p-3 text-right font-semibold">Neto</th>
                          <th className="p-3 text-right font-semibold">Total</th>
                          <th className="p-3 text-center font-semibold">SII</th>
                          <th className="p-3 text-center font-semibold">Pago</th>
                          <th className="p-3 text-center font-semibold hidden lg:table-cell">Email</th>
                          <th className="p-3 text-left font-semibold hidden lg:table-cell">Fecha</th>
                          <th className="p-3 text-center font-semibold">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dtes.map(dte => (
                          <tr key={dte.id} className="border-b hover:bg-muted/30">
                            <td className="p-3 font-mono font-bold text-blue-600">#{dte.document_number || '-'}</td>
                            <td className="p-3">
                              <Badge variant="outline" className="text-xs whitespace-nowrap">
                                {docTypeLabels[String(dte.document_type_id)] || dte.document_type_label}
                              </Badge>
                            </td>
                            <td className="p-3 text-xs font-medium max-w-[200px] truncate">{dte.empresa_razon_social}</td>
                            <td className="p-3 font-mono text-xs hidden md:table-cell">{dte.empresa_rut}</td>
                            <td className="p-3 text-right font-mono">${fmt(dte.total_neto)}</td>
                            <td className="p-3 text-right font-mono font-bold">${fmt(dte.total)}</td>
                            <td className="p-3 text-center">
                              {dte.estado_sii && (
                                <Badge className={`text-xs ${siiColors[dte.estado_sii] || 'bg-gray-500/10 text-gray-500'}`}>
                                  {dte.estado_sii}
                                </Badge>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <Badge
                                className={`text-xs cursor-pointer hover:opacity-80 transition-opacity ${estadoPagoColors[dte.estado_pago] || estadoPagoColors.desconocido}`}
                                onClick={() => handleTogglePago(dte)}
                                title="Clic para cambiar estado de pago"
                                data-testid={`pago-${dte.id}`}
                              >
                                {dte.estado_pago === 'pagado' ? <CreditCard className="h-3 w-3 mr-1" /> : null}
                                {dte.estado_pago || '-'}
                              </Badge>
                            </td>
                            <td className="p-3 text-center hidden lg:table-cell">
                              {dte.email_enviado ? <MailCheck className="h-4 w-4 text-green-500 mx-auto" /> : <span className="text-muted-foreground text-xs">-</span>}
                            </td>
                            <td className="p-3 text-xs hidden lg:table-cell">{dte.issue_date}</td>
                            <td className="p-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {dte.facto_document_id && (
                                  <Button size="sm" variant="ghost" onClick={() => handleViewRaw(dte)} title="Ver JSON Facto" data-testid={`raw-${dte.id}`}>
                                    <Code className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" onClick={() => factoDescargarPdf(dte.id).catch(e => toast.error(e.message))} title="PDF" data-testid={`pdf-${dte.id}`}>
                                  <Download className="h-4 w-4" />
                                </Button>
                                {dte.document_type_id !== 16 && !dte.referencia_dte_id && (
                                  <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700"
                                    onClick={() => { if (window.confirm('¿Emitir Nota de Crédito?')) factoEmitirNotaCredito({ dte_id: dte.id }).then(nc => { toast.success(`NC #${nc.document_number}`); loadDtes(); }).catch(e => toast.error(e.message)); }}
                                    title="Nota de Crédito" data-testid={`nc-${dte.id}`}>
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                )}
                                {dte.referencia_document_number && (
                                  <span className="text-xs text-muted-foreground ml-1">Ref #{dte.referencia_document_number}</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between" data-testid="dtes-pagination">
                    <div className="text-xs text-muted-foreground">
                      Mostrando {dtesPage * PAGE_SIZE + 1}-{Math.min((dtesPage + 1) * PAGE_SIZE, dtesTotal)} de {dtesTotal}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" disabled={dtesPage === 0} onClick={() => setDtesPage(0)} data-testid="dtes-first-page">
                        1
                      </Button>
                      <Button size="sm" variant="outline" disabled={dtesPage === 0} onClick={() => setDtesPage(p => p - 1)} data-testid="dtes-prev-page">
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm font-medium px-3 py-1 bg-muted rounded">
                        {dtesPage + 1} / {totalPages}
                      </span>
                      <Button size="sm" variant="outline" disabled={dtesPage >= totalPages - 1} onClick={() => setDtesPage(p => p + 1)} data-testid="dtes-next-page">
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" disabled={dtesPage >= totalPages - 1} onClick={() => setDtesPage(totalPages - 1)} data-testid="dtes-last-page">
                        {totalPages}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ====== Emit Dialog ====== */}
        <Dialog open={!!emitDialog} onOpenChange={() => setEmitDialog(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-blue-500" /> Emitir DTEs - Lote {emitDialog?.lote?.numero_lote}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Se emitirán DTEs para <strong>{emitDialog?.lote?.total_empresas}</strong> empresas
                por un total de <strong>${fmt(emitDialog?.lote?.total)}</strong>
              </div>

              <div>
                <Label className="text-sm font-medium">Tipo de Documento</Label>
                <Select value={docTypeId} onValueChange={setDocTypeId}>
                  <SelectTrigger data-testid="emit-doc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {docTypeOptions.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {giroProfiles.length > 0 && (
                <div>
                  <Label className="text-sm font-medium">Perfil de Giro</Label>
                  <Select value={selectedGiro} onValueChange={setSelectedGiro}>
                    <SelectTrigger data-testid="emit-giro"><SelectValue placeholder="Giro por defecto" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Giro por defecto (config)</SelectItem>
                      {giroProfiles.map(g => (
                        <SelectItem key={g.id} value={g.id}>
                          <div className="flex items-center gap-2">
                            {g.logo_url && <img src={g.logo_url} alt="" className="h-4 w-4 rounded object-cover" />}
                            {g.nombre} — {g.giro}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <Checkbox id="send-email" checked={sendEmail} onCheckedChange={setSendEmail} data-testid="emit-send-email" />
                  <label htmlFor="send-email" className="text-sm flex items-center gap-2 cursor-pointer">
                    <Mail className="h-4 w-4 text-blue-500" /> Enviar PDF por email al cliente
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox id="create-linkify" checked={createLinkify} onCheckedChange={setCreateLinkify} data-testid="emit-create-linkify" />
                  <label htmlFor="create-linkify" className="text-sm flex items-center gap-2 cursor-pointer">
                    <Link className="h-4 w-4 text-green-500" /> Crear cobro en Linkify (conciliación)
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmitDialog(null)}>Cancelar</Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                disabled={emittingLote === emitDialog?.loteId}
                onClick={() => handleEmitirFacto(emitDialog?.loteId)}
                data-testid="btn-confirm-emit"
              >
                {emittingLote ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Emitir {emitDialog?.lote?.total_empresas} DTEs
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ====== Detalle Dialog ====== */}
        <Dialog open={!!detalleLote} onOpenChange={() => setDetalleLote(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Lote {detalleLote?.numero_lote} - Detalle</DialogTitle>
            </DialogHeader>
            {detalleLote && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Badge className={estadoColors[detalleLote.estado] || ''}>{detalleLote.estado}</Badge>
                  <Badge variant="outline">{tipoLabels[detalleLote.tipo]}</Badge>
                  {detalleLote.mes && <span className="text-sm">{MESES[detalleLote.mes - 1]} {detalleLote.anio}</span>}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="p-2 text-left">RUT</th>
                        <th className="p-2 text-left">Razón Social</th>
                        <th className="p-2 text-left">Glosa</th>
                        <th className="p-2 text-right">Neto</th>
                        <th className="p-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detalleLote.empresas_resumen || []).map((e, i) => (
                        <tr key={i} className="border-b">
                          <td className="p-2 font-mono">{e.rut_formateado || e.rut}</td>
                          <td className="p-2">{e.razon_social}</td>
                          <td className="p-2 text-muted-foreground">{e.glosa}</td>
                          <td className="p-2 text-right font-mono">${fmt(e.total_neto)}</td>
                          <td className="p-2 text-right font-mono font-bold">${fmt(e.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="font-bold border-t-2">
                      <td colSpan={3} className="p-2">TOTAL</td>
                      <td className="p-2 text-right font-mono">${fmt(detalleLote.total_neto)}</td>
                      <td className="p-2 text-right font-mono">${fmt(detalleLote.total)}</td>
                    </tr></tfoot>
                  </table>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetalleLote(null)}>Cerrar</Button>
              <Button onClick={() => descargarExcelFacto(detalleLote?.id)} className="bg-emerald-600 hover:bg-emerald-700">
                <Download className="h-4 w-4 mr-2" /> Excel
              </Button>
              {factoConnected && detalleLote?.estado === 'pendiente' && (
                <Button onClick={() => { setEmitDialog({ loteId: detalleLote.id, lote: detalleLote }); setDetalleLote(null); }} className="bg-blue-600 hover:bg-blue-700">
                  <Send className="h-4 w-4 mr-2" /> Emitir DTEs
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ====== Sync Dialog ====== */}
        <Dialog open={syncDialog} onOpenChange={setSyncDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-blue-500" /> Sincronizar documentos desde Facto
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {factoInfoData && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 bg-muted/30 rounded-lg text-center">
                    <div className="text-xl font-black">{factoInfoData.total_facto?.toLocaleString('es-CL')}</div>
                    <div className="text-xs text-muted-foreground">Total en Facto API</div>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg text-center">
                    <div className="text-xl font-black">{factoInfoData.total_local?.toLocaleString('es-CL')}</div>
                    <div className="text-xs text-muted-foreground">En base local</div>
                  </div>
                </div>
              )}

              <p className="text-sm text-muted-foreground">
                Importa documentos emitidos desde la API de Facto a tu base local.
                Puedes filtrar por rango de fechas para no traer todo el historial.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Fecha desde</Label>
                  <Input type="date" value={syncDateFrom} onChange={e => setSyncDateFrom(e.target.value)} className="h-9" data-testid="sync-date-from" />
                </div>
                <div>
                  <Label className="text-xs">Fecha hasta</Label>
                  <Input type="date" value={syncDateTo} onChange={e => setSyncDateTo(e.target.value)} className="h-9" data-testid="sync-date-to" />
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Sin fechas = importa todos los documentos emitidos (puede tomar varios minutos con miles de documentos).
              </p>

              {syncResult && (
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg space-y-1">
                  <div className="text-sm font-medium text-green-700">{syncResult.message}</div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center">
                      <div className="font-bold text-lg">{syncResult.imported}</div>
                      <div className="text-muted-foreground">Importados</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-lg">{syncResult.skipped}</div>
                      <div className="text-muted-foreground">Ya existían</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-lg">{syncResult.total_available?.toLocaleString('es-CL')}</div>
                      <div className="text-muted-foreground">Total en Facto</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSyncDialog(false)}>Cerrar</Button>
              <Button onClick={handleSync} disabled={syncing} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-exec-sync">
                {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Database className="h-4 w-4 mr-2" />}
                {syncing ? 'Sincronizando...' : 'Sincronizar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ====== Raw JSON Dialog ====== */}
        <Dialog open={!!rawJsonDialog} onOpenChange={() => setRawJsonDialog(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Code className="h-5 w-5 text-orange-500" /> JSON Facto — Doc #{rawJsonDialog?.document_number}
              </DialogTitle>
            </DialogHeader>
            {rawJsonLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : rawJsonData ? (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Dato crudo del documento en la API de Facto (Koywe). Campos de estado:
                  <strong> document_status</strong>, <strong>taxbureau_validation_status</strong>, <strong>payment_conditions</strong>.
                </p>
                <pre className="bg-muted p-4 rounded-lg overflow-auto text-xs font-mono max-h-[60vh] whitespace-pre-wrap" data-testid="raw-json-content">
                  {JSON.stringify(rawJsonData, null, 2)}
                </pre>
              </div>
            ) : null}
            <DialogFooter>
              <Button variant="outline" onClick={() => setRawJsonDialog(null)}>Cerrar</Button>
              {rawJsonData && (
                <Button variant="outline" onClick={() => { navigator.clipboard.writeText(JSON.stringify(rawJsonData, null, 2)); toast.success('JSON copiado al portapapeles'); }}>
                  Copiar JSON
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ====== Capturar Patentes Dialog ====== */}
        <Dialog open={capturaDialog} onOpenChange={setCapturaDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Car className="h-5 w-5 text-purple-500" /> Capturar Patentes desde Facturas
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-purple-500/5 border border-purple-500/10 rounded-lg space-y-2">
                <p className="text-sm font-medium text-purple-700">¿Cómo funciona?</p>
                <ol className="text-xs text-muted-foreground space-y-1 list-decimal pl-4">
                  <li>Lee las facturas emitidas en Facto para el mes seleccionado</li>
                  <li>Busca patentes (ej: ABCD-12, AB-1234) en el detalle de cada línea de factura</li>
                  <li>Asocia cada patente a la empresa correspondiente por su RUT</li>
                  <li>Crea los dispositivos (vehículos) nuevos en tu base de datos</li>
                </ol>
                <p className="text-xs text-muted-foreground">
                  Usa "Solo previsualizar" primero para ver qué encontrará sin modificar datos.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-36">
                  <Label className="text-xs">Mes</Label>
                  <Select value={String(capturaMes)} onValueChange={v => setCapturaMes(Number(v))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{MESES.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="w-24">
                  <Label className="text-xs">Año</Label>
                  <Input type="number" value={capturaAnio} onChange={e => setCapturaAnio(Number(e.target.value))} className="h-9" />
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex items-center gap-2">
                    <Checkbox id="dry-run" checked={capturaDryRun} onCheckedChange={setCapturaDryRun} />
                    <label htmlFor="dry-run" className="text-sm cursor-pointer">Solo previsualizar</label>
                  </div>
                </div>
              </div>

              <Button onClick={handleCapturar} disabled={capturando} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-exec-captura">
                {capturando ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                {capturaDryRun ? 'Previsualizar' : 'Capturar y Asignar'}
              </Button>

              {capturaProgress && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">{capturaProgress.progress}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center">
                      <div className="font-bold">{capturaProgress.docs_found}</div>
                      <div className="text-muted-foreground">Encontradas</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold">{capturaProgress.docs_processed}</div>
                      <div className="text-muted-foreground">Procesadas</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-purple-600">{capturaProgress.patentes_found}</div>
                      <div className="text-muted-foreground">Patentes</div>
                    </div>
                  </div>
                  {capturaProgress.docs_found > 0 && (
                    <div className="w-full bg-muted rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full transition-all" style={{width: `${Math.min(100, (capturaProgress.docs_processed / capturaProgress.docs_found) * 100)}%`}} />
                    </div>
                  )}
                </div>
              )}

              {capturaResult && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Card><CardContent className="p-3 text-center">
                      <div className="text-xl font-black">{capturaResult.documentos_procesados}</div>
                      <div className="text-xs text-muted-foreground">Facturas leídas</div>
                    </CardContent></Card>
                    <Card><CardContent className="p-3 text-center">
                      <div className="text-xl font-black text-purple-600">{capturaResult.total_patentes_encontradas}</div>
                      <div className="text-xs text-muted-foreground">Patentes encontradas</div>
                    </CardContent></Card>
                    <Card><CardContent className="p-3 text-center">
                      <div className="text-xl font-black text-green-600">{capturaResult.dispositivos_creados}</div>
                      <div className="text-xs text-muted-foreground">Dispositivos nuevos</div>
                    </CardContent></Card>
                    <Card><CardContent className="p-3 text-center">
                      <div className="text-xl font-black">{capturaResult.empresas_actualizadas}</div>
                      <div className="text-xs text-muted-foreground">Empresas actualizadas</div>
                    </CardContent></Card>
                  </div>

                  {capturaResult.dry_run && (
                    <div className="p-2 bg-yellow-500/10 text-yellow-700 text-xs rounded-md text-center font-medium">
                      PREVISUALIZACION — Los datos no se guardaron. Desactiva &quot;Solo previsualizar&quot; para aplicar.
                    </div>
                  )}

                  {capturaResult.empresas?.length > 0 && (() => {
                    const allFiltered = capturaResult.empresas.flatMap(e => e.filtered || []);
                    return (
                    <div className="space-y-3">
                      <div className="overflow-x-auto max-h-[50vh]">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b bg-muted/50 sticky top-0">
                              <th className="p-2 text-left">RUT</th>
                              <th className="p-2 text-left">Empresa</th>
                              <th className="p-2 text-left">Patentes aceptadas</th>
                              {allFiltered.length > 0 && <th className="p-2 text-left">Filtradas (revisar)</th>}
                              <th className="p-2 text-center">Estado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {capturaResult.empresas.map((e, i) => (
                              <tr key={i} className="border-b align-top">
                                <td className="p-2 font-mono">{e.rut}</td>
                                <td className="p-2">{e.razon_social}</td>
                                <td className="p-2">
                                  <div className="flex flex-wrap gap-1">
                                    {e.patentes?.map((p, j) => {
                                      const off = excludedPatentes.has(p);
                                      return (
                                        <Badge key={j} variant="outline"
                                          className={`text-xs font-mono cursor-pointer select-none transition-all ${off ? 'opacity-30 line-through bg-red-500/5' : 'bg-green-500/10 border-green-500/30'}`}
                                          title={e.sources?.[p] ? `"${e.sources[p]}"` : ''}
                                          onClick={() => setExcludedPatentes(prev => { const n = new Set(prev); if (n.has(p)) n.delete(p); else n.add(p); return n; })}
                                        >{p}</Badge>
                                      );
                                    })}
                                  </div>
                                  {e.patentes?.filter(p => e.sources?.[p]).map((p, j) => (
                                    <div key={j} className={`text-[10px] text-muted-foreground mt-0.5 ${excludedPatentes.has(p) ? 'line-through opacity-30' : ''}`}>
                                      {p}: &ldquo;{e.sources[p]}&rdquo;
                                    </div>
                                  ))}
                                </td>
                                {allFiltered.length > 0 && (
                                  <td className="p-2">
                                    {e.filtered?.length > 0 ? (
                                      <div className="space-y-1">
                                        <div className="flex flex-wrap gap-1">
                                          {e.filtered.map((p, j) => {
                                            const on = includedFiltered.has(p);
                                            return (
                                              <Badge key={j} variant="outline"
                                                className={`text-xs font-mono cursor-pointer select-none transition-all ${on ? 'bg-green-500/10 border-green-500/30' : 'opacity-40 bg-yellow-500/5 border-yellow-500/20'}`}
                                                title={e.sources?.[p] ? `"${e.sources[p]}"` : ''}
                                                onClick={() => setIncludedFiltered(prev => { const n = new Set(prev); if (n.has(p)) n.delete(p); else n.add(p); return n; })}
                                              >{p}</Badge>
                                            );
                                          })}
                                        </div>
                                        {e.filtered.map((p, j) => (
                                          <div key={j} className={`text-[10px] text-muted-foreground ${includedFiltered.has(p) ? 'text-green-600' : 'opacity-50'}`}>
                                            {p}: &ldquo;{e.sources?.[p] || '?'}&rdquo;
                                          </div>
                                        ))}
                                      </div>
                                    ) : <span className="text-muted-foreground">—</span>}
                                  </td>
                                )}
                                <td className="p-2 text-center">
                                  <Badge className={e.status === 'ok' ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}>
                                    {e.status === 'ok' ? 'OK' : 'Sin empresa'}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCapturaDialog(false)}>Cerrar</Button>
              {capturaResult?.dry_run && capturaResult.total_patentes_encontradas > 0 && (() => {
                const finalCount = capturaResult.total_patentes_encontradas - excludedPatentes.size + includedFiltered.size;
                return (
                <>
                  {(excludedPatentes.size > 0 || includedFiltered.size > 0) && (
                    <div className="p-2 bg-muted/50 border rounded-md text-xs space-y-1">
                      {excludedPatentes.size > 0 && <div className="text-red-600">Excluidas: {[...excludedPatentes].join(', ')}</div>}
                      {includedFiltered.size > 0 && <div className="text-green-600">Re-incluidas: {[...includedFiltered].join(', ')}</div>}
                    </div>
                  )}
                  <Button className="bg-purple-600 hover:bg-purple-700"
                    disabled={capturando || finalCount <= 0}
                    onClick={() => {
                      setCapturando(true);
                      setCapturaResult(null);
                      setCapturaProgress(null);
                      // Excluded = user-excluded from accepted + all auto-filtered except re-included
                      const allFilteredPats = capturaResult.empresas.flatMap(e => e.filtered || []);
                      const finalExcluded = [...excludedPatentes, ...allFilteredPats.filter(p => !includedFiltered.has(p))];
                      factoCapturarPatentes({
                        mes: capturaMes, anio: capturaAnio, dry_run: false, max_pages: 20,
                        excluded_patentes: finalExcluded,
                      }).then(r => {
                        if (r.task_id) {
                          let retries = 0;
                          const poll = setInterval(async () => {
                            try {
                              const s = await factoCapturarPatentesStatus(r.task_id);
                              retries = 0;
                              setCapturaProgress(s);
                              if (s.status === 'done') { clearInterval(poll); setCapturaResult(s.result); setCapturaProgress(null); setCapturando(false); toast.success(s.result.message); }
                              else if (s.status === 'error') { clearInterval(poll); setCapturaProgress(null); setCapturando(false); toast.error(s.progress || 'Error'); }
                            } catch { retries++; if (retries > 5) { clearInterval(poll); setCapturando(false); toast.error('Sin respuesta'); } }
                          }, 3000);
                        }
                      }).catch(e => { toast.error(e.message); setCapturando(false); });
                    }}
                    data-testid="btn-apply-captura"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Aplicar ({finalCount} patentes)
                  </Button>
                </>
                );
              })()}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </G4sLayout>
  );
}
